import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { X, Plus, Minus, Trash2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useCart } from "../contexts/CartContext";

const CartDrawer = () => {
  const { open, setOpen, items, updateQty, remove, subtotal, shipping, total } = useCart();
  const navigate = useNavigate();

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 bg-black/70 z-50"
            data-testid="cart-overlay"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.35 }}
            className="fixed right-0 top-0 h-full w-full sm:w-[420px] bg-[#0a0a0a] border-l border-white/10 z-50 flex flex-col"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <p className="font-mono-cap text-sm">Your Bag ({items.length})</p>
              <button data-testid="cart-close" onClick={() => setOpen(false)}><X size={18} /></button>
            </div>

            <div className="flex-1 overflow-y-auto">
              {items.length === 0 ? (
                <div className="text-center py-20 px-6">
                  <p className="font-serif text-2xl">Your bag is empty.</p>
                  <p className="text-white/50 text-sm mt-2">Time to find the drip.</p>
                  <button
                    data-testid="cart-shop-now"
                    onClick={() => { setOpen(false); navigate("/collections"); }}
                    className="mt-8 bg-white text-black font-mono-cap text-xs px-6 py-3 hover:bg-white/90"
                  >
                    Shop Now
                  </button>
                </div>
              ) : (
                <ul className="divide-y divide-white/5">
                  {items.map((it) => (
                    <li key={it.key} className="p-5 flex gap-4" data-testid={`cart-item-${it.key}`}>
                      <div className="w-20 h-24 bg-white/5 overflow-hidden">
                        <img src={it.image} alt={it.name} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">{it.name}</p>
                        <p className="text-white/50 text-xs mt-1">Size: {it.size}</p>
                        <p className="text-sm mt-2">₹{it.price * it.quantity}</p>
                        <div className="flex items-center gap-2 mt-3">
                          <button onClick={() => updateQty(it.key, it.quantity - 1)} className="w-7 h-7 border border-white/15 flex items-center justify-center hover:bg-white/5" data-testid={`qty-dec-${it.key}`}><Minus size={12} /></button>
                          <span className="text-sm w-6 text-center" data-testid={`qty-${it.key}`}>{it.quantity}</span>
                          <button onClick={() => updateQty(it.key, it.quantity + 1)} className="w-7 h-7 border border-white/15 flex items-center justify-center hover:bg-white/5" data-testid={`qty-inc-${it.key}`}><Plus size={12} /></button>
                          <button onClick={() => remove(it.key)} className="ml-auto text-white/40 hover:text-white" data-testid={`remove-${it.key}`}><Trash2 size={14} /></button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-white/10 p-5 space-y-3">
                <div className="flex justify-between text-sm"><span className="text-white/60">Subtotal</span><span data-testid="cart-subtotal">₹{subtotal}</span></div>
                <div className="flex justify-between text-sm"><span className="text-white/60">Shipping</span><span>{shipping === 0 ? "Free" : `₹${shipping}`}</span></div>
                <div className="flex justify-between text-base font-medium border-t border-white/10 pt-3"><span>Total</span><span data-testid="cart-total">₹{total}</span></div>
                <button
                  data-testid="cart-checkout"
                  onClick={() => { setOpen(false); navigate("/checkout"); }}
                  className="w-full bg-white text-black font-mono-cap text-xs py-4 hover:bg-white/90"
                >
                  Checkout — ₹{total}
                </button>
                <Link to="/collections" onClick={() => setOpen(false)} className="block text-center text-xs text-white/50 hover:text-white">Continue shopping</Link>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;
